import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import { api } from '../../services/api';
import { useLang } from '../../i18n/context';
import type { Event } from '../../types';

const CITY_COORDS: Record<string, [number, number, number]> = {
  'Bruxelles': [50.850, 4.350, 12],
  'Gent': [51.054, 3.717, 13],
  'Antwerpen': [51.219, 4.402, 13],
  'Brugge': [51.209, 3.225, 13],
  'Bruges': [51.209, 3.225, 13],
  'Liège': [50.633, 5.580, 13],
  'Namur': [50.467, 4.872, 13],
  'Charleroi': [50.411, 4.445, 13],
  'Leuven': [50.880, 4.700, 13],
  'Kortemark': [51.024, 3.045, 13],
  'Houthalen': [51.034, 5.378, 13],
  'Oostende': [51.225, 2.926, 13],
  'Paris': [48.857, 2.352, 12],
};

const R = '#d4213d';
const G = '#c9a84c';
const V = '#1a8c38';

function daysUntil(dateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr + 'T00:00:00');
  return Math.round((target.getTime() - today.getTime()) / 86400000);
}

function getColor(dateStr: string): string {
  const d = daysUntil(dateStr);
  if (d <= 7) return V;   // nearest → green
  if (d <= 30) return G;  // this month → gold
  return R;               // far out → red
}

function getRadius(_dateStr: string): number {
  return 6;
}

function getGlowPx(dateStr: string): number {
  const d = daysUntil(dateStr);
  if (d <= 1) return 14;
  if (d <= 3) return 10;
  if (d <= 7) return 7;
  if (d <= 14) return 5;
  if (d <= 30) return 3;
  return 1;
}

function shouldPulse(dateStr: string): boolean {
  return daysUntil(dateStr) === 0; // Only today's events
}

function shortDate(dateStr: string, t: (key: string, params?: Record<string, string | number>) => string): string {
  const d = new Date(dateStr);
  const months = t('date.months').split(',');
  const day = d.getDate();
  const month = months[d.getMonth()] || '';
  return `${day} ${month.slice(0, 3)}`;
}

function fullDate(dateStr: string, t: (key: string, params?: Record<string, string | number>) => string): string {
  const d = new Date(dateStr);
  const days = t('date.days').split(',');
  const months = t('date.months').split(',');
  return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

function countdown(dateStr: string, t: (key: string, params?: Record<string, string | number>) => string): string {
  const d = daysUntil(dateStr);
  if (d === 0) return t('map.today_excl');
  if (d === 1) return t('map.tomorrow');
  if (d <= 7) return t('map.in_days', { n: d });
  if (d <= 30) return t('map.in_weeks', { n: Math.ceil(d / 7) });
  return t('map.in_months', { n: Math.ceil(d / 30) });
}

function popupHTML(event: Event, t: (key: string, params?: Record<string, string | number>) => string): string {
  const color = getColor(event.date);
  const tags = (event.genre_tags || []).map(g => {
    const bg = g === 'dub' ? `${V}33` : g === 'reggae' ? `${G}28` : g === 'roots' ? '#5a3e1e44' : `${R}22`;
    const fg = g === 'dub' ? V : g === 'reggae' ? G : g === 'roots' ? '#c9a84c' : R;
    return `<span style="display:inline-block;padding:2px 7px;border-radius:4px;font-size:10px;font-weight:600;background:${bg};color:${fg};margin:0 3px 3px 0;text-transform:uppercase;letter-spacing:0.5px;">${g}</span>`;
  }).join('');

  const artists = (event.artists || []).map((a: any) =>
    `<span style="display:inline-block;padding:2px 6px;border-radius:3px;font-size:11px;background:rgba(255,255,255,0.06);color:#e0d0a0;margin:0 3px 3px 0;">
      ${a.is_sound_system ? '&#128266;' : '&#127908;'} ${a.name}</span>`
  ).join('');

  return `
    <div style="font-family:'Space Grotesk',sans-serif;min-width:250px;max-width:320px;padding:14px;color:#e0e0e0;">
      <div style="font-size:11px;color:${color};font-weight:700;text-transform:uppercase;letter-spacing:1.2px;font-family:'JetBrains Mono',monospace;margin-bottom:6px;">
        ${fullDate(event.date, t)}
      </div>
      <div style="font-size:15px;font-weight:700;margin-bottom:6px;line-height:1.3;color:#fff;">
        ${event.title}
      </div>
      <div style="margin-bottom:8px;">
        <span style="background:${color}18;color:${color};padding:2px 8px;border-radius:4px;font-size:10px;font-weight:700;border:1px solid ${color}44;">
          ${countdown(event.date, t)}
        </span>
      </div>
      ${(event.venue_name || event.venue_address) ? `<div style="font-size:12px;color:#a0a0b0;margin-bottom:6px;">&#128205; <strong>${event.venue_name || event.venue_address}</strong>${event.city || event.venue_city ? ` — ${event.city || event.venue_city}` : ''}</div>` : ''}
      ${event.is_free ? `<div style="font-size:12px;margin-bottom:8px;"><span style="color:${V};font-weight:700;">&#10003; ${t('common.free').toUpperCase()}</span></div>` : (event.price_info && event.price_info !== '0') ? `<div style="font-size:12px;margin-bottom:8px;"><span style="color:${G};">&#127915; ${event.price_info}</span></div>` : (event.price && event.price > 0) ? `<div style="font-size:12px;margin-bottom:8px;"><span style="color:${G};">&#127915; ${event.price}€</span></div>` : ''}
      ${tags ? `<div style="margin-bottom:6px;">${tags}</div>` : ''}
      ${artists ? `<div>${artists}</div>` : ''}
      ${event.source_url ? `<div style="margin-top:10px;padding-top:8px;border-top:1px solid rgba(255,255,255,0.08);"><a href="${event.source_url}" target="_blank" rel="noopener noreferrer" style="color:${G};font-size:11px;text-decoration:none;">&#128279; Source</a></div>` : ''}
    </div>
  `;
}

// Inject pulse animation CSS once
let cssInjected = false;
function injectPulseCSS() {
  if (cssInjected) return;
  cssInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes radar-pulse {
      0% { transform: translate(-50%, -50%) scale(1); opacity: 0.7; }
      70% { transform: translate(-50%, -50%) scale(1.6); opacity: 0; }
      100% { transform: translate(-50%, -50%) scale(1.6); opacity: 0; }
    }
    @keyframes radar-glow {
      0% { box-shadow: 0 0 3px 1px currentColor; }
      50% { box-shadow: 0 0 8px 3px currentColor; }
      100% { box-shadow: 0 0 3px 1px currentColor; }
    }
    .pulse-dot {
      border-radius: 50%;
      position: relative;
      cursor: pointer;
      transition: transform 0.2s;
    }
    .pulse-dot:hover {
      transform: scale(1.3) !important;
      z-index: 1000 !important;
    }
    .pulse-ring {
      position: absolute;
      border-radius: 50%;
      border: 2px solid currentColor;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      animation: radar-pulse 2s ease-out infinite;
      pointer-events: none;
    }
    .pulse-dot.pulsing {
      animation: radar-glow 2s ease-in-out infinite;
    }
    .rasta-popup .leaflet-popup-content-wrapper {
      background: #0a0a18ee;
      border: 1px solid #c9a84c33;
      border-radius: 8px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.6);
    }
    .rasta-popup .leaflet-popup-tip {
      background: #0a0a18ee;
      border: 1px solid #c9a84c22;
    }
    .rasta-popup .leaflet-popup-close-button {
      color: #888 !important;
    }
  `;
  document.head.appendChild(style);
}

interface MapTabProps {
  focusCity?: string | null;
  onClearCity?: () => void;
}

export function MapTab({ focusCity, onClearCity }: MapTabProps) {
  const { t } = useLang();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [freeOnly, setFreeOnly] = useState(false);

  useEffect(() => { loadEvents(); }, []);
  useEffect(() => { injectPulseCSS(); }, []);

  useEffect(() => {
    if (focusCity === undefined) return;
    const map = mapInstanceRef.current;
    if (!map) return;
    if (focusCity && CITY_COORDS[focusCity]) {
      const [lat, lng, zoom] = CITY_COORDS[focusCity];
      map.flyTo([lat, lng], zoom, { duration: 1.2 });
    } else if (focusCity === null) {
      map.flyTo([50.7, 4.4], 8, { duration: 1.0 });
    }
  }, [focusCity]);

  // Init map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    const map = L.map(mapRef.current, { center: [50.7, 4.4], zoom: 8, zoomControl: true });
    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; OpenStreetMap &copy; CARTO',
      maxZoom: 19,
    }).addTo(map);
    mapInstanceRef.current = map;
    return () => { map.remove(); mapInstanceRef.current = null; };
  }, []);

  // Render markers
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Clear existing markers
    map.eachLayer(layer => {
      if (layer instanceof L.Marker || layer instanceof L.CircleMarker) map.removeLayer(layer);
    });

    const isFreeEvent = (e: Event) => e.is_free || e.price_info?.toLowerCase().includes('gratuit') || e.price_info?.toLowerCase().includes('free');
    const filteredEvents = freeOnly ? events.filter(e => isFreeEvent(e)) : events;
    const geoEvents = filteredEvents.filter(e => e.latitude && e.longitude);
    // Render furthest first so closest are on top (higher z-index)
    const sorted = [...geoEvents].sort((a, b) =>
      Math.abs(new Date(b.date).getTime() - Date.now()) - Math.abs(new Date(a.date).getTime() - Date.now())
    );

    for (const event of sorted) {
      const color = getColor(event.date);
      const radius = getRadius(event.date);
      const glowPx = getGlowPx(event.date);
      const pulse = shouldPulse(event.date);

      const dotSize = radius * 2;
      const ringExtra = pulse ? 10 : 0;
      const totalSize = dotSize + ringExtra + 8;

      const html = `
        <div class="pulse-dot ${pulse ? 'pulsing' : ''}" style="
          width:${dotSize}px;
          height:${dotSize}px;
          background:${color};
          color:${color};
          border: 1px solid rgba(255,255,255,${pulse ? 0.5 : 0.15});
          box-shadow: 0 0 ${glowPx}px ${Math.ceil(glowPx / 2)}px ${color}${glowPx > 8 ? 'cc' : glowPx > 4 ? '88' : '44'};
        ">
          ${pulse ? `
            <div class="pulse-ring" style="
              width:${dotSize + 8}px;
              height:${dotSize + 8}px;
              color:${color};
            "></div>
          ` : ''}
        </div>
      `;

      const icon = L.divIcon({
        className: 'radar-marker',
        html,
        iconSize: [totalSize, totalSize],
        iconAnchor: [totalSize / 2, totalSize / 2],
        popupAnchor: [0, -radius - 4],
      });

      const marker = L.marker([event.latitude!, event.longitude!], { icon }).addTo(map);

      // Tooltip on hover (date + title)
      marker.bindTooltip(
        `<div style="font-family:'JetBrains Mono',monospace;font-size:10px;color:${color};font-weight:700;letter-spacing:0.5px;">${shortDate(event.date, t)}</div>
         <div style="font-size:12px;font-weight:600;max-width:200px;white-space:normal;line-height:1.3;">${event.title}</div>
         ${event.venue_name ? `<div style="font-size:10px;color:#888;">📍 ${event.venue_name}</div>` : ''}`,
        {
          direction: 'top',
          offset: [0, -radius - 8],
          className: 'radar-tooltip',
          opacity: 1,
        }
      );

      // Popup on click
      marker.bindPopup(popupHTML(event, t), {
        className: 'rasta-popup',
        maxWidth: 340,
        minWidth: 250,
      });
    }
  }, [events, t, freeOnly]);

  async function loadEvents() {
    try {
      const today = new Date().toISOString().split('T')[0];
      const data = await api.getEvents({ status: 'validated', from: today, limit: '200' });
      setEvents(data);
    } catch (err) {
      console.error('Failed to load events for map:', err);
    }
  }

  const isFreeCheck = (e: Event) => e.is_free || e.price_info?.toLowerCase().includes('gratuit') || e.price_info?.toLowerCase().includes('free');
  const displayEvents = freeOnly ? events.filter(e => isFreeCheck(e)) : events;
  const geoCount = displayEvents.filter(e => e.latitude && e.longitude).length;

  return (
    <div>
      <div className="section-header">
        <span className="section-title">&#128506; {t('map.title')}</span>
        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
          {geoCount} {t('map.geolocated')} {events.length}
        </span>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: '20px', marginBottom: '10px', fontSize: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: V, boxShadow: `0 0 8px 3px ${V}aa`, border: '1px solid rgba(255,255,255,0.5)' }} />
          <span style={{ color: V, fontWeight: 600 }}>{t('map.today')}</span>
          <span style={{ color: 'var(--text-muted)', fontSize: '10px' }}>{t('map.pulsing')}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: V, boxShadow: `0 0 5px ${V}66`, border: '1px solid rgba(255,255,255,0.15)' }} />
          <span style={{ color: V, fontWeight: 600 }}>{t('map.this_week')}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: G, boxShadow: `0 0 3px ${G}44` }} />
          <span style={{ color: G, fontWeight: 600 }}>{t('map.this_month')}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: R, boxShadow: `0 0 1px ${R}33` }} />
          <span style={{ color: R, fontWeight: 600 }}>{t('map.later')}</span>
        </div>
        {/* FREE rotary knob — only if free events exist */}
        {events.some(e => e.is_free || e.price_info?.toLowerCase().includes('gratuit') || e.price_info?.toLowerCase().includes('free')) && (
          <FreeKnob active={freeOnly} onClick={() => setFreeOnly(!freeOnly)} />
        )}
      </div>

      {/* Map */}
      <div style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--border-color)' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', zIndex: 500, background: `linear-gradient(to right, ${R}, ${G}, ${V})` }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '3px', zIndex: 500, background: `linear-gradient(to right, ${V}, ${G}, ${R})` }} />
        <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: '3px', zIndex: 500, background: `linear-gradient(to bottom, ${R}, ${G}, ${V})` }} />
        <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: '3px', zIndex: 500, background: `linear-gradient(to bottom, ${V}, ${G}, ${R})` }} />
        <div className="map-container" ref={mapRef} style={{ height: 'calc(100vh - 320px)' }} />
      </div>
    </div>
  );
}

// ─── FREE Analog Meter ───────────────────────────────────────────────────────

function FreeKnob({ active, onClick }: { active: boolean; onClick: () => void }) {
  return (
    <div onClick={onClick} title={active ? 'Show all events' : 'Show FREE events only'}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '4px 12px 3px', borderRadius: '6px', cursor: 'pointer', userSelect: 'none',
        background: active ? 'linear-gradient(180deg, #1a2e1a 0%, #0e1a0e 100%)' : 'linear-gradient(180deg, #1e1e22 0%, #111114 100%)',
        border: `1.5px solid ${active ? '#4caf50' : '#333'}`,
        boxShadow: active ? '0 0 14px rgba(76,175,80,0.4)' : '0 2px 4px rgba(0,0,0,0.3)',
        transition: 'all 0.3s', marginLeft: '8px',
      }}>
      <div style={{
        width: '44px', height: '22px', position: 'relative',
        borderRadius: '22px 22px 0 0', overflow: 'hidden',
        background: active
          ? 'radial-gradient(ellipse at 50% 100%, #d4e8d0 0%, #a8c8a0 40%, #6a9a60 100%)'
          : 'radial-gradient(ellipse at 50% 100%, #d0ccc0 0%, #a8a498 40%, #6a6860 100%)',
        border: `1px solid ${active ? '#4caf5066' : '#44444466'}`,
        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.4)', transition: 'background 0.3s',
      }}>
        {[0,1,2,3,4].map(i => (
          <div key={i} style={{ position: 'absolute', bottom: 0, left: '50%', width: '0.5px', height: i%2===0?'5px':'3px', background: '#333', transformOrigin: 'bottom center', transform: `rotate(${-60+i*30}deg)` }} />
        ))}
        <div style={{
          position: 'absolute', bottom: '-1px', left: '50%', width: '1.5px', height: '18px', marginLeft: '-0.75px',
          background: active ? '#c62828' : '#333', borderRadius: '1px 1px 0 0',
          transformOrigin: 'bottom center', transform: `rotate(${active ? 50 : -50}deg)`,
          transition: 'transform 0.5s cubic-bezier(0.4,0,0.2,1), background 0.3s',
        }} />
        <div style={{ position: 'absolute', bottom: '-2px', left: '50%', width: '5px', height: '5px', marginLeft: '-2.5px', borderRadius: '50%', background: 'radial-gradient(circle at 40% 35%, #888 0%, #333 100%)' }} />
      </div>
      <span style={{ fontSize: '8px', fontWeight: 800, letterSpacing: '1.5px', marginTop: '2px', color: active ? '#4caf50' : '#555', textShadow: active ? '0 0 6px rgba(76,175,80,0.5)' : 'none', transition: 'all 0.3s', textTransform: 'uppercase' }}>
        Free Events
      </span>
    </div>
  );
}
